import type { FilterOptionsState } from "@mui/material";
import { useMemo } from "react";

import type { ISelectListItem } from "@ryde/shared";

import {
  filterOptionsByLabelOrTechnicalName,
  getOptionTechnicalNames,
} from "./dropdown-bound.utils";

/**
 * For select items that carry a `technicalName`: the code name per option (for hover) and a filter
 * that matches it next to the label. `filterOptions` is undefined when no item has one, so the
 * dropdown keeps MUI's own filter.
 */
export const useTechnicalNameOptions = <TOption>({
  selectItems,
  getOptionLabel,
}: {
  selectItems: ISelectListItem[] | undefined;
  getOptionLabel: (option: TOption) => string;
}): {
  technicalNames: ReadonlyMap<unknown, string>;
  filterOptions:
    | ((options: TOption[], state: FilterOptionsState<TOption>) => TOption[])
    | undefined;
} => {
  const technicalNames = useMemo(
    () => getOptionTechnicalNames(selectItems),
    [selectItems],
  );

  const filterOptions = useMemo(
    () =>
      technicalNames.size
        ? (
            options: TOption[],
            { inputValue }: FilterOptionsState<TOption>,
          ): TOption[] =>
            filterOptionsByLabelOrTechnicalName({
              options,
              inputValue,
              getOptionLabel,
              technicalNames,
            })
        : undefined,
    [technicalNames, getOptionLabel],
  );

  return { technicalNames, filterOptions };
};
